import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  useLayoutEffect,
} from "react"
import { Grid, Button, Icon, Loader, Dimmer } from "semantic-ui-react"
import styled, { keyframes } from "styled-components"
import Sorting from "../Sorting"
import ProductCard from "../ProductCard"
import { device } from "../../theme"
import { FilterContext } from "../../providers/FilterProvider"
import useWindowSize from "../../hooks/useWindowSize"
import { handleCharacters } from "../../utils/handleCharacters"
import { throttle } from "../../utils"
import useScroll from "../../hooks/fetchOnScroll"
import { GenderContext } from "../../providers/GenderProvider"
import logo from "../../images/favicon.png"
import { BrandsContext } from "../../providers/BrandsProvider"
import { LoadingContext } from "../../providers/LoadingProducts"
import { SortContext } from "../../providers/SortingProvider"

const Container = styled.div`
  width: 100%;
  position: relative;
  min-height: calc(100vh - 120px);
  padding-left: 22px;
  margin-top: 30px;

  @media ${device.tablet} {
    padding-left: 0;
    margin-top: 30px;
  }
`

const Column = styled(Grid.Column)`
  &.five.wide.computer.eight.wide.mobile.five.wide.tablet.column {
    width: 25% !important;

    &:nth-child(even) {
      padding: 32px 12px 0;
    }

    &:nth-child(odd) {
      padding: 12px;
    }

    @media ${device.tablet} {
      width: 33.333% !important;
    }

    @media ${device.phone} {
      width: 50% !important;
      padding: 6px !important;
    }
  }
`

const StyledGrid = styled(Grid)`
  position: relative;
  overflow: hidden;
  //transition: max-height 1s ease-in-out;
  //max-height: ${props => props.height}px;
  justify-content: space-between;

  &::after {
    content: "";
    flex: auto;
  }

  padding-bottom: 110px !important;
  @media ${device.tablet} {
    max-height: ${props => props.height + 720}px;
  }

  @media ${device.phone} {
    max-height: ${props => props.height + 70}px;
  }
`

const ScrollToTop = styled.button`
  position: ${props => (props.isFixed ? "fixed" : "absolute")};
  bottom: 22px;
  right: ${props => (props.isFixed ? "22px" : "1rem")};
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  outline: none;
  cursor: pointer;
  height: 60px;
  width: 60px;
  border-radius: 50%;
  box-shadow: 0 0 0 6px rgb(255, 99, 71, 0.3);
  background: ${props => props.theme.colors.green};
`

const fadeIn = keyframes`
0% {
  transform: rotate(0deg);
  box-shadow: 0 0 0 6px rgba(255, 99, 71, 0.1);
}

50% {
  box-shadow: 0 0 0 6px rgba(255, 99, 71, 0.9);
}

100% {
  transform: rotate(360deg);
  box-shadow: 0 0 0 6px rgba(255, 99, 71, 0.1);
}
`

const LoadingImg = styled.img`
  display: block;
  border: 6px solid white;
  height: 100px;
  width: 100px;
  margin-left: -60px;
  margin-top: -60px;
  position: absolute;
  top: 50%;
  left: 50%;
  border-radius: 50%;
  animation: ${fadeIn} 2s linear infinite;

  @media ${device.tablet} {
    top: 30%;
  }
`

function Products({ gender, category }) {
  const readMoreRef = useRef()
  const containerRef = useRef()
  const { state, dispatch } = useContext(FilterContext)
  const { sortBy } = useContext(SortContext)
  const { loadingProducts } = useContext(LoadingContext)
  const [showBackToTop, setBackToTop] = useState(false)
  const [position, setPosition] = useState("fixed")
  const [pageNumber, setReset] = useScroll()
  const [isLoading, setLoading] = useState(true)
  const [hasError, setError] = useState(false)

  const isElementInViewport = element => {
    let el = null
    if (!element) {
      el = readMoreRef?.current
    } else {
      el = element
    }

    if (!el) return
    const rect = el.getBoundingClientRect()
    const isInView =
      rect.bottom > 0 &&
      rect.right > 0 &&
      rect.left < (window.innerWidth || document.documentElement.clientWidth) &&
      rect.top < (window.innerHeight || document.documentElement.clientHeight)

    return isInView
  }

  const showScrollToTop = () => {
    const footer = document.querySelector("footer")
    if (isElementInViewport(footer)) {
      setPosition("absolute")
      return
    }

    if (isElementInViewport()) {
      setPosition("absolute")
    } else {
      setPosition("fixed")
    }

    const fromTop = containerRef.current?.getBoundingClientRect().top
    if (fromTop < -100) {
      setBackToTop(true)
    } else {
      setBackToTop(false)
    }
  }

  const handleFeed = () => {
    setError(false)

    let url = `https://static8-api.herokuapp.com/lineout/feed?gender=${
      gender === "herre" ? "male" : "female"
    }&sortBy=${state.sortBy}&size=20&page=${pageNumber}&category=${category}`

    if (state.selectedBrands.length) {
      const encodeBrands = encodeURI(JSON.stringify([...state.selectedBrands]))
      url = `https://static8-api.herokuapp.com/lineout/search?gender=${
        gender === "herre" ? "men" : "women"
      }&sortBy=${
        state.sortBy
      }&size=20&page=${pageNumber}&category=${category}&brands=${encodeBrands}`
    }

    fetch(url)
      .then(res => res.json())
      .then(e => {
        console.log("res", e)
        if (e.err) {
          setError(true)
          return
        }

        if (!e.length) {
          setLoading(false)
          return
        }
        if (state.resetState) {
          dispatch({
            resetState: false,
            products: [...e],
          })
          return
        }

        dispatch({
          products: [...state.products, ...e],
        })
      })
      .catch(err => {
        console.log(err)
        setLoading(false)
      })
  }

  useEffect(() => {
    if (!isLoading) return
    handleFeed()
  }, [pageNumber])

  useEffect(() => {
    setReset(true)
    setLoading(true)
  }, [state.selectedBrands, sortBy])

  useEffect(() => {
    if (!hasError) return
    console.log("Error catched. Refetch api")
    handleFeed()
  }, [hasError])

  useEffect(() => {
    document.body.addEventListener(
      "scroll",
      throttle(showScrollToTop, 50),
      false
    )
    return () => {
      dispatch({ resetState: true })
      document.body.removeEventListener(
        "scroll",
        throttle(showScrollToTop, 50),
        false
      )
    }
  }, [])

  if (!state.products.length || loadingProducts) {
    return (
      <Container>
        <LoadingImg src={logo} alt="Loading" />
      </Container>
    )
  }
  console.log(state.products)
  return (
    <>
      <Container ref={containerRef}>
        {/* {width > 1024 && <Sorting />} */}
        <StyledGrid id="products">
          {state.products.map((product, key) => (
            <Column
              key={`${product.productId}-${key}`}
              className="product-card"
              mobile={8}
              tablet={5}
              computer={5}
            >
              <ProductCard
                affiliate={product.productAffiliate}
                store={product.productStore}
                productPath={product.productPath}
                categoryPage={product.categoryPage}
                productCategory={product.productCategory}
                productKey={key}
                id={product.productId}
                shopId={product.productStoreKey}
                brand={product.productBrand}
                price={product.productPrice}
                oldPrice={
                  product.productOldPrice &&
                  Number(product.productOldPrice) > Number(product.productPrice)
                    ? product.productOldPrice
                    : null
                }
                title={handleCharacters(product.productName)}
                image={product.productImage}
                url={product.productUrl}
              />
            </Column>
          ))}
          {showBackToTop && (
            <ScrollToTop
              isFixed={position === "fixed"}
              onClick={() => document.body.scrollTo(0, 0)}
            >
              <Icon
                style={{ color: "#fff", margin: 0 }}
                name="arrow up"
                size="large"
              />
            </ScrollToTop>
          )}
        </StyledGrid>

        {isLoading && (
          <div style={{ position: "relative" }}>
            <Loader active size="big" />
          </div>
        )}
      </Container>
    </>
  )
}

export default Products
